// src/pages/Runsheet-page/Runsheet.js
import React, { useState, useEffect, useMemo } from 'react';
import BASE_URL from '../../api';
import { toast } from 'react-toastify';
import { FaEye, FaEdit, FaPrint, FaSearch } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SiteLogo from '../../assets/images/SparkLogistics.jpg';

const RUNSHEET_FIELDS = [
  { key: 'runsheetno', label: 'Runsheet No', type: 'text', readonly: true },
  { key: 'rundate', label: 'Run Date', type: 'date', readonly: true },
  {
    key: 'deliverystaffid',
    label: 'Delivery Staff',
    type: 'select',
    options: [],
    readonly: false
  },
  { key: 'vehicleno', label: 'Vehicle No', type: 'text', readonly: true },
  { key: 'driver', label: 'Driver', type: 'text', readonly: true },
  { key: 'mobileno', label: 'Mobile No', type: 'text', readonly: true },
  { key: 'vehicletypeid', label: 'Vehicle Type', type: 'text', readonly: true },
  {
    key: 'status',
    label: 'Status',
    type: 'select-enum',
    options: [
      'Manifest Out - Intransit',
      'Manifest In - Reached at Destination',
      'Runsheet - Out of Delivery',
      'Delivered',
      'Pending',
      'Return to Hub',
      'Wrong Address'
    ],
    readonly: false
  }
];


const Runsheet = () => {
  const token = localStorage.getItem('token');
  const API_BASE = `${BASE_URL}/api`;

  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [runsheets, setRunsheets] = useState([]);
  const [deliveryUsers, setDeliveryUsers] = useState([]);
  const [selectedRunsheet, setSelectedRunsheet] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    _id: '', runsheetno: '', rundate: '', deliverystaffid: '',
    vehicleno: '', driver: '', mobileno: '', vehicletypeid: '',
    allottedlist: '', status: ''
  });

  const [selectedBookingIds, setSelectedBookingIds] = useState(new Set());
  const [attachedBookings, setAttachedBookings] = useState([]);
  const [editedConsignors, setEditedConsignors] = useState({});
  const [editedBookingRemarks, setEditedBookingRemarks] = useState({});

  const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';

  // ────── FETCH DELIVERY USERS ──────
  const fetchDeliveryUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/master/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setDeliveryUsers(json.data);
      }
    } catch (err) {
      console.error('Error fetching delivery users:', err);
    }
  };

  // ────── FETCH RUNSHEETS ──────
  const fetchRunsheets = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/getctrl/getrunsheet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setRunsheets(json.data);
      } else {
        toast.error(json.message || 'Failed to load runsheets');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDeliveryUsers();
      fetchRunsheets();
    }
  }, [token]);

  // ────── FIXED: CORRECT DRIVER & VEHICLE MAPPING (Based on Example JSON) ──────
  const getVehicleInfo = (rs) => {
    // 1. Check for manual override at runsheet level
    if (rs.vehicleno && rs.vehicleno.trim() !== '' && rs.vehicleno !== 'Not Assigned') {
      return {
        vehicleno: rs.vehicleno || 'Not Assigned',
        driver: rs.driver || 'Not Assigned',
        mobileno: rs.mobileno || 'Not Assigned',
        vehicletypeid: typeof rs.vehicletypeid === 'string' ? rs.vehicletypeid : (rs.vehicletypeid === 1 ? 'Truck' : rs.vehicletypeid === 0 ? 'Tempo' : 'Not Assigned')
      };
    }

    // 2. Fallback to Nested Manifest / Route / Transport Info
    const manifest = rs?.ingoingManifestId?.outgoingManifestId;
    const route = manifest?.routeid;
    const transport = manifest?.transportid;

    return {
      vehicleno: transport?.vehicleno || manifest?.vehiclenumber || route?.vehiclenumberText || 'Not Assigned',
      driver: manifest?.drivername || route?.drivername || 'Not Assigned',
      mobileno: transport?.mobileno || route?.mobileno || 'Not Assigned',
      vehicletypeid: manifest?.vehicletypeid || (route?.vehicletypeid === 1 ? 'Truck' : 'Tempo')
    };
  };

  const getStaffName = (staffId) => {
    if (!staffId) return 'Not Assigned';
    const u = deliveryUsers.find(u => u._id === staffId);
    return u?.username || 'Not Assigned';
  };

  const getAttachedBookings = (rs) => {
    return Array.isArray(rs?.ingoingManifestId?.onlineBooking)
      ? rs.ingoingManifestId.onlineBooking
      : [];
  };

  const [currentEditRunsheet, setCurrentEditRunsheet] = useState(null);

  // ────── EDIT HANDLER ──────
  const handleEdit = async (rs) => {
    setCurrentEditRunsheet(rs);
    await fetchDeliveryUsers();
    const v = getVehicleInfo(rs);
    const bookings = getAttachedBookings(rs);

    setFormData({
      _id: rs._id,
      runsheetno: rs.runsheetno,
      rundate: rs.rundate ? new Date(rs.rundate).toISOString().split('T')[0] : '',
      deliverystaffid: rs.deliverystaffid || '',
      vehicleno: v.vehicleno,
      driver: v.driver,
      mobileno: v.mobileno,
      vehicletypeid: v.vehicletypeid,
      allottedlist: rs.allottedlist || '',
      status: rs.status || ''
    });

    setAttachedBookings(bookings);
    const defaultSelected = new Set(bookings.map(b => b._id));
    setSelectedBookingIds(defaultSelected);

    const cons = {};
    const rem = {};
    bookings.forEach(b => {
      cons[b._id] = b.consigneeTOaddress || b.consigneeid?.companyName || b.consigneeid?.name || b.consignor || '';
      rem[b._id] = b.bookingremarks || '';
    });
    setEditedConsignors(cons);
    setEditedBookingRemarks(rem);

    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setCurrentEditRunsheet(null);
    setAttachedBookings([]);
    setSelectedBookingIds(new Set());
    setEditedConsignors({});
    setEditedBookingRemarks({});
    setFormData({
      _id: '', runsheetno: '', rundate: '', deliverystaffid: '',
      vehicleno: '', driver: '', mobileno: '', vehicletypeid: '',
      allottedlist: '', status: ''
    });
  };

  // ────── REMOVE BOOKING ──────
  const handleRemoveBooking = async (bookingId) => {
    try {
      const payload = { action: "remove", onlineIds: [bookingId] };
      const url = `${API_BASE}/outgoingmanifests/updaterunsheet/${formData._id}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (res.ok && json.success) {
        setRunsheets(prev => prev.map(r =>
          r._id === formData._id
            ? { ...r, online: (r.online || []).filter(id => id !== bookingId) }
            : r
        ));
        setSelectedBookingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(bookingId);
          return newSet;
        });
        toast.success('Booking removed');
        return true;
      } else {
        toast.error(json.message || 'Remove failed');
        return false;
      }
    } catch (err) {
      toast.error('Network error');
      return false;
    }
  };

  const toggleBookingSelection = async (bookingId) => {
    const isSelected = selectedBookingIds.has(bookingId);
    if (isSelected) {
      const newSet = new Set(selectedBookingIds);
      newSet.delete(bookingId);
      setSelectedBookingIds(newSet);

      const success = await handleRemoveBooking(bookingId);
      if (!success) {
        setSelectedBookingIds(prev => new Set([...prev, bookingId]));
      }
    } else {
      toast.info('Adding new bookings is not allowed here.');
    }
  };

  // ────── SUBMIT UPDATE ──────
  const handleSubmit = async () => {
    if (!formData._id) return toast.error('Runsheet ID missing');
    if (!formData.deliverystaffid) return toast.error('Select delivery staff');
    if (!formData.status) return toast.error('Select status');

    const currentRunsheet = runsheets.find(r => r._id === formData._id);
    const currentOnline = new Set(currentRunsheet?.online?.map(String) || []);
    const selectedOnline = Array.from(selectedBookingIds).map(String);
    const toRemove = [...currentOnline].filter(id => !selectedOnline.includes(id));

    const v = getVehicleInfo(currentRunsheet);
    const selectedBookings = attachedBookings.filter(b => selectedBookingIds.has(b._id));

    const allConsignors = selectedBookings
      .map(b => editedConsignors[b._id] || b.consignor || '')
      .filter(Boolean)
      .join(', ');
    const allRemarks = selectedBookings
      .map(b => editedBookingRemarks[b._id] || b.bookingremarks || '')
      .filter(Boolean)
      .join(' | ');

    const payload = {
      deliverystaffid: formData.deliverystaffid,
      status: formData.status,
      vehicletypeid: v.vehicletypeid === 'Truck' ? 1 : 0,
      vehicleno: v.vehicleno,
      driver: v.driver,
      mobileno: v.mobileno,
      consignors: allConsignors,
      bookingremarks: allRemarks
    };

    if (toRemove.length > 0) {
      payload.action = "remove";
      payload.onlineIds = toRemove;
    }

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE}/outgoingmanifests/updaterunsheet/${formData._id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (res.ok && json.success) {
        toast.success('Runsheet updated successfully');
        await fetchRunsheets();
        handleCloseForm();
      } else {
        toast.error(json.message || 'Update failed');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  // ────── PRINT PDF (Fixed: Alignment Issues) ──────
  const handlePrint = (rs) => {
    try {
      const v = getVehicleInfo(rs);
      const staff = getStaffName(rs.deliverystaffid);
      const bookings = getAttachedBookings(rs);

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;

      const generatePDF = (hasLogo) => {
        // 1. Header Area
        if (hasLogo) {
          doc.addImage(img, 'JPEG', margin, 5, 45, 22);
        }

        // Company Info (Top Right)
        doc.setFontSize(22);
        doc.setTextColor(41, 128, 185); // Blue theme
        doc.setFont('helvetica', 'bold');
        doc.text('SPARK LOGISTICS', pageWidth - margin, 15, { align: 'right' });

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text('RUNSHEET - DELIVERY CHALLAN', pageWidth - margin, 21, { align: 'right' });

        // Horizontal Divider
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, 28, pageWidth - margin, 28);

        // 2. Runsheet Metadata Grid (Properly Aligned)
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);

        // Left Column Positions
        const labelX = margin;
        const colonX = margin + 25;
        const valueX = margin + 28;

        // Right Column Positions
        const labelX2 = pageWidth / 2 + 5;
        const colonX2 = pageWidth / 2 + 30;
        const valueX2 = pageWidth / 2 + 33;

        // Labels
        doc.setFont('helvetica', 'bold');
        doc.text('Runsheet No', labelX, 36);
        doc.text('Date', labelX, 41);
        doc.text('Staff', labelX, 46);

        doc.text('Vehicle No', labelX2, 36);
        doc.text('Driver', labelX2, 41);
        doc.text('Mobile', labelX2, 46);

        // Colons
        doc.setFont('helvetica', 'normal');
        doc.text(':', colonX, 36);
        doc.text(':', colonX, 41);
        doc.text(':', colonX, 46);
        doc.text(':', colonX2, 36);
        doc.text(':', colonX2, 41);
        doc.text(':', colonX2, 46);

        // Values
        doc.text(`${rs.runsheetno}`, valueX, 36);
        doc.text(`${formatDate(rs.rundate)}`, valueX, 41);
        doc.text(`${staff}`, valueX, 46);
        doc.text(`${v.vehicleno}`, valueX2, 36);
        doc.text(`${v.driver}`, valueX2, 41);
        doc.text(`${v.mobileno}`, valueX2, 46);

        // 3. Table Generation
        const rows = bookings.map((b, i) => {
          const outConsignors = rs?.ingoingManifestId?.outgoingManifestId?.consignors;
          const consignorList = outConsignors ? outConsignors.split(',').map(s => s.trim()) : [];
          const customConsignee = consignorList[i] && consignorList[i] !== 'N/A' ? consignorList[i] : null;

          const consignorName = b.pickrequestid?.clientid?.companyName || 
                                b.pickrequestid?.clientid?.name || 
                                b.consignorid?.companyName || 
                                b.consignorid?.name || 
                                b.clientid?.companyName || 
                                b.clientid?.name || 
                                (typeof b.consignorid === 'string' ? b.consignorid : null) ||
                                b.consignor || 
                                '-';

          const rawConsignee = b.consigneeTOaddress || 
                               b.consigneeid?.companyName || 
                               b.consigneeid?.name || 
                               (typeof b.consigneeid === 'string' && b.consigneeid !== 'N/A' ? b.consigneeid : null) || 
                               b.consignee;

          let consigneeName = '-';
          if (customConsignee && customConsignee.trim() !== '' && customConsignee !== 'N/A' && customConsignee !== consignorName) {
            consigneeName = customConsignee;
          } else if (rawConsignee && rawConsignee.trim() !== '' && rawConsignee !== '-' && rawConsignee !== consignorName) {
            consigneeName = rawConsignee;
          }

          return [
            i + 1,
            b.docketNumber || '-',
            consigneeName,
            consignorName,
            b.pickrequestid?.location || b.fromLocation?.split(' (')[0] || '-',
            rs?.ingoingManifestId?.outgoingManifestId?.desthubid?.branchname || b.topincode?.locationname || '-',
            b.noofbox || '-',
            (b.actweight || 0).toFixed(1),
            b.mode || '-',
            b.ewaybill || '-',
            b.bookingremarks || '-'
          ];
        });

        autoTable(doc, {
          head: [['S.No', 'AWB / Docket', 'Consignee', 'Consignor', 'Origin', 'Destination', 'Box', 'Wt', 'Mode', 'E-Way Bill', 'Remarks']],
          body: rows,
          startY: 53,
          theme: 'grid',
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontSize: 8,
            halign: 'center',
            fontStyle: 'bold'
          },
          bodyStyles: { fontSize: 7, valign: 'middle' },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 20, fontStyle: 'bold' },
            6: { cellWidth: 10, halign: 'center' },
            7: { cellWidth: 10, halign: 'center' },
            8: { cellWidth: 15, halign: 'center' },
            9: { cellWidth: 20 },
            10: { cellWidth: 'auto' }
          },
          margin: { left: margin, right: margin }
        });

        // 4. Summary and Signatures
        const finalY = doc.lastAutoTable.finalY + 15;
        const totalWt = bookings.reduce((s, b) => s + (b.actweight || 0), 0).toFixed(1);
        const totalBox = bookings.reduce((s, b) => s + (b.noofbox || 0), 0);

        // Summary Box
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, finalY - 5, 95, 18, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(`TOTAL SUMMARY`, margin + 5, finalY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`Total Bookings: ${bookings.length}  |  Boxes: ${totalBox}  |  Weight: ${totalWt} kg`, margin + 5, finalY + 6);

        // Signature Lines
        const sigY = finalY + 25;

        // Authorized Sig (Left)
        doc.line(margin, sigY + 15, margin + 60, sigY + 15);
        doc.text('Authorized Signatory', margin + 30, sigY + 20, { align: 'center' });

        // Staff Sig (Right)
        doc.line(pageWidth - margin - 60, sigY + 15, pageWidth - margin, sigY + 15);
        doc.text('Delivery Staff Signature', pageWidth - margin - 30, sigY + 20, { align: 'center' });

        // 5. Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(`Page ${i} of ${pageCount} - Generated by Spark Logistics System`, pageWidth / 2, 287, { align: 'center' });
        }

        doc.save(`Runsheet_${rs.runsheetno}.pdf`);
        toast.success('Professional Runsheet Generated');
      };

      // Load Logo and generate
      const img = new Image();
      img.src = SiteLogo;
      img.onload = () => generatePDF(true);
      img.onerror = () => {
        console.warn("Logo failed to load, generating PDF without it.");
        generatePDF(false);
      };

    } catch (err) {
      console.error('Print error:', err);
      toast.error('Print failed');
    }
  };

  // ────── RENDER FIELD ──────
  const renderField = (field) => {
    const value = formData[field.key] || '';
    const editable = ['deliverystaffid', 'status'].includes(field.key);
    const onChange = e => setFormData(p => ({ ...p, [field.key]: e.target.value }));

    if (field.key === 'deliverystaffid' && editable) {
      return (
        <select value={value} onChange={onChange} className="w-full p-2 border rounded text-sm">
          <option value="">Select Staff</option>
          {deliveryUsers.map(u => (
            <option key={u._id} value={u._id}>
              {u.username} ({u.phone})
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'select-enum' && editable) {
      return (
        <select value={value} onChange={onChange} className="w-full p-2 border rounded text-sm">
          {field.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }

    return (
      <input
        type={field.type}
        value={value}
        readOnly={!editable}
        onChange={onChange}
        className={`w-full p-2 border rounded text-sm ${!editable ? 'bg-gray-100' : ''}`}
      />
    );
  };

  // ────── SEARCH & PAGINATION ──────
  const filtered = useMemo(() => {
    if (!searchTerm) return runsheets;
    const s = searchTerm.toLowerCase();
    return runsheets.filter(r => {
      const v = getVehicleInfo(r);
      const staff = getStaffName(r.deliverystaffid);
      const count = getAttachedBookings(r).length;
      return (
        r.runsheetno?.toLowerCase().includes(s) ||
        v.vehicleno.toLowerCase().includes(s) ||
        v.driver.toLowerCase().includes(s) ||
        staff.toLowerCase().includes(s) ||
        count.toString().includes(s)
      );
    });
  }, [runsheets, searchTerm, deliveryUsers]);



  const filteredRunsheets = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    if (!s) return runsheets;
    return runsheets.filter(r => {
      const v = getVehicleInfo(r);
      const staff = getStaffName(r.deliverystaffid);
      return (
        r.runsheetno?.toLowerCase().includes(s) ||
        v.vehicleno?.toLowerCase().includes(s) ||
        v.driver?.toLowerCase().includes(s) ||
        staff.toLowerCase().includes(s) ||
        r.status?.toLowerCase().includes(s)
      );
    });
  }, [runsheets, searchTerm, deliveryUsers]);

  const paginated = filteredRunsheets.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredRunsheets.length / itemsPerPage);


  const selectedCount = selectedBookingIds.size;
  const totalBoxes = attachedBookings
    .filter(b => selectedBookingIds.has(b._id))
    .reduce((s, b) => s + (b.noofbox || 0), 0);
  const totalWeight = attachedBookings
    .filter(b => selectedBookingIds.has(b._id))
    .reduce((s, b) => s + (b.actweight || 0), 0).toFixed(1);

  // ────── RENDER ──────
  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Runsheet Master</h2>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <div className="relative w-full md:w-80">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search runsheet, vehicle, driver..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
            </div>
          </div>

          {/* Table UI - Consistent with Pincode/Outgoing Master */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">S.No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Runsheet No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Run Date</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Delivery Staff</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Vehicle / Driver</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                  <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-500 font-medium">Loading runsheets...</td></tr>
                ) : paginated.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">No runsheets found</td></tr>
                ) : (
                  paginated.map((r, i) => {
                    const v = getVehicleInfo(r);
                    const staff = getStaffName(r.deliverystaffid);
                    const count = getAttachedBookings(r).length;

                    // S.No Logic: Search Persistence Fix
                    const sNo = searchTerm
                      ? runsheets.findIndex(orig => orig._id === r._id) + 1
                      : (currentPage - 1) * itemsPerPage + i + 1;

                    return (
                      <tr key={r._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                        <td className="p-3 font-bold text-blue-700">{r.runsheetno}</td>
                        <td className="p-3 text-gray-700 font-medium">{formatDate(r.rundate)}</td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800">{staff}</span>
                            <span className="text-[10px] text-gray-500 uppercase">{count} Bookings Included</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800 uppercase text-[11px]">{v.vehicleno}</span>
                            <span className="text-[10px] text-blue-600 italic font-medium">{v.driver}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${r.status?.includes('Delivered') ? 'bg-green-100 text-green-700' :
                              r.status?.includes('Out of Delivery') ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                            }`}>
                            {r.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => { setSelectedRunsheet(r); setShowViewModal(true); }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition" title="View">
                              <FaEye size={16} />
                            </button>
                            <button onClick={() => handleEdit(r)}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition" title="Edit">
                              <FaEdit size={16} />
                            </button>
                            <button onClick={() => handlePrint(r)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition" title="Print PDF">
                              <FaPrint size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <span className="text-sm text-gray-600 font-medium">
                Showing <span className="text-blue-600">{Math.min(filteredRunsheets.length, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="text-blue-600">{Math.min(filteredRunsheets.length, currentPage * itemsPerPage)}</span> of {filteredRunsheets.length} entries
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* EDIT MODAL */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-xl">
              <h2 className="text-xl font-bold mb-4">Update Runsheet: {formData.runsheetno}</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {RUNSHEET_FIELDS.map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium mb-1">
                      {f.label} {['deliverystaffid', 'status'].includes(f.key) && '*'}
                    </label>
                    {renderField(f)}
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-semibold">
                    Attached Bookings ({attachedBookings.length})
                  </h3>
                </div>

                <div className="border rounded max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 w-8"></th>
                        <th className="p-2">S.No</th>
                        <th className="p-2">Docket No</th>
                        <th className="p-2">Origin</th>
                        <th className="p-2">Dest</th>
                        <th className="p-2">Consignee</th>
                        <th className="p-2">Mode</th>
                        <th className="p-2">Pin</th>
                        <th className="p-2">Box</th>
                        <th className="p-2">Wt</th>
                        <th className="p-2">E-Way</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachedBookings.map((b, i) => {
                        const isChecked = selectedBookingIds.has(b._id);
                        return (
                          <tr key={b._id} className={`border-b ${isChecked ? 'bg-blue-50' : ''}`}>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleBookingSelection(b._id)}
                                className="w-4 h-4"
                                disabled={!isChecked}
                              />
                            </td>
                            <td className="p-2 text-center">{i + 1}</td>
                            <td className="p-2 font-medium">{b.docketNumber}</td>
                            <td className="p-2">{b.pickrequestid?.location || b.fromLocation?.split(' (')[0] || '-'}</td>
                            <td className="p-2">{currentEditRunsheet?.ingoingManifestId?.outgoingManifestId?.desthubid?.branchname || b.topincode?.locationname || '-'}</td>
                            <td className="p-2">{b.consigneeTOaddress || b.consigneeid?.companyName || b.consigneeid?.name || b.consigneeid?.email || '-'}</td>
                            <td className="p-2">{b.mode || '-'}</td>
                            <td className="p-2">{b.topincode?.pincode || '-'}</td>
                            <td className="p-2 text-center">{b.noofbox || 0}</td>
                            <td className="p-2">{(b.actweight || 0).toFixed(1)}</td>
                            <td className="p-2">{b.ewaybill || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="p-3 bg-gray-50 text-xs border-t">
                    <strong>Selected:</strong> {selectedCount} pcs |
                    <strong> Boxes:</strong> {totalBoxes} |
                    <strong> Weight:</strong> {totalWeight} kg
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={handleCloseForm}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm">Cancel</button>
                <button onClick={handleSubmit}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm">
                  {isLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* VIEW MODAL */}
        {showViewModal && selectedRunsheet && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
              <h2 className="text-xl font-bold mb-4">Runsheet: {selectedRunsheet.runsheetno}</h2>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                {RUNSHEET_FIELDS.map(f => {
                  let val = selectedRunsheet[f.key];
                  if (f.key === 'deliverystaffid') val = getStaffName(val);
                  else if (['vehicleno', 'driver', 'mobileno', 'vehicletypeid'].includes(f.key)) {
                    const v = getVehicleInfo(selectedRunsheet);
                    val = v[f.key];
                  }
                  return <p key={f.key}><strong>{f.label}:</strong> {val || 'N/A'}</p>;
                })}
              </div>

              {/* Attached Bookings Table */}
              <div className="mt-4 border-t pt-4">
                <h3 className="text-md font-semibold mb-2">Attached Bookings ({getAttachedBookings(selectedRunsheet).length})</h3>
                <div className="border rounded max-h-60 overflow-y-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-center">S.No</th>
                        <th className="p-2">Docket No</th>
                        <th className="p-2">Origin</th>
                        <th className="p-2">Dest</th>
                        <th className="p-2">Consignee</th>
                        <th className="p-2">Consignor</th>
                        <th className="p-2">Mode</th>
                        <th className="p-2 text-center">Box</th>
                        <th className="p-2">Wt (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getAttachedBookings(selectedRunsheet).map((b, i) => (
                        <tr key={b._id || i} className="border-b hover:bg-gray-50">
                          <td className="p-2 text-center">{i + 1}</td>
                          <td className="p-2 font-medium text-blue-700">{b.docketNumber || '-'}</td>
                          <td className="p-2">{b.pickrequestid?.location || b.fromLocation?.split(' (')[0] || '-'}</td>
                          <td className="p-2">{selectedRunsheet?.ingoingManifestId?.outgoingManifestId?.desthubid?.branchname || b.topincode?.locationname || '-'}</td>
                          <td className="p-2">{b.consigneeTOaddress || b.consigneeid?.companyName || b.consigneeid?.name || '-'}</td>
                          <td className="p-2">{b.pickrequestid?.clientid?.companyName || b.pickrequestid?.clientid?.name || b.consignorid?.companyName || b.consignorid?.name || b.consignor || '-'}</td>
                          <td className="p-2">{b.mode || '-'}</td>
                          <td className="p-2 text-center">{b.noofbox || 0}</td>
                          <td className="p-2">{(b.actweight || 0).toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button onClick={() => setShowViewModal(false)}
                className="mt-4 px-4 py-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600">Close</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Runsheet;